import { useCallback, useEffect, useRef, useState } from 'react';

import { IUser } from '../../../../models/user/IUser';
import { detectPlatform } from '../../utils/detectPlatform';
import { readStandaloneDisplayModeFromWindow } from '../../utils/isStandaloneDisplayMode';
import { reportPwaInstall } from '../api/reportPwaInstall';
import { IPwaInstallReportResult } from '../types';
import {
    markInstallPending,
    markInstallReported,
    shouldReportPwaInstall,
} from '../utils/pwaInstallLocalState';

export interface IUsePwaInstallTrackingOptions {
    user: IUser | undefined;
    token: string | undefined;
    win?: Window;
    reportFn?: typeof reportPwaInstall;
}

export interface IUsePwaInstallTrackingResult {
    lastResult: IPwaInstallReportResult | null;
    bonusDialogOpen: boolean;
    closeBonusDialog: () => void;
    isReporting: boolean;
}

/**
 * When the user is authenticated and running as an installed PWA,
 * report once to the backend and surface a bonus dialog if coins were credited.
 */
export function usePwaInstallTracking(options: IUsePwaInstallTrackingOptions): IUsePwaInstallTrackingResult {
    const { user, token, win = window, reportFn = reportPwaInstall } = options;
    const [lastResult, setLastResult] = useState<IPwaInstallReportResult | null>(null);
    const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
    const [isReporting, setIsReporting] = useState(false);
    const inFlightRef = useRef(false);

    useEffect(() => {
        const onAppInstalled = () => {
            markInstallPending(win.localStorage);
        };
        win.addEventListener('appinstalled', onAppInstalled);
        return () => win.removeEventListener('appinstalled', onAppInstalled);
    }, [win]);

    useEffect(() => {
        const userId = user?.Userid;
        if (!user || !token || !userId || inFlightRef.current) {
            return;
        }

        const isStandalone = readStandaloneDisplayModeFromWindow(win);
        if (
            !shouldReportPwaInstall({
                userId,
                isStandalone,
                storage: win.localStorage,
            })
        ) {
            return;
        }

        inFlightRef.current = true;
        setIsReporting(true);

        const platform = detectPlatform({
            userAgent: win.navigator.userAgent,
            maxTouchPoints: win.navigator.maxTouchPoints,
            platform: win.navigator.platform,
        });

        void (async () => {
            try {
                const result = await reportFn({ user, token, platform });
                setLastResult(result);

                if (result.status === 'credited' || result.status === 'already_installed') {
                    markInstallReported(userId, win.localStorage);
                }

                if (result.status === 'credited' && result.bonusCoins > 0) {
                    setBonusDialogOpen(true);
                }
            } finally {
                inFlightRef.current = false;
                setIsReporting(false);
            }
        })();
    }, [user, token, win, reportFn]);

    const closeBonusDialog = useCallback(() => setBonusDialogOpen(false), []);

    return {
        lastResult,
        bonusDialogOpen,
        closeBonusDialog,
        isReporting,
    };
}
